import React, { useMemo } from "react";
import DashboardCardWrapper from "../../common/DashboardCardWrapper";
import DashboardCardHeader from "../../common/DashboardCardHeader";
import BorderedIconWrapper from "../../common/BorderIconWrapper";
import { CardContent } from "../../ui/card";
import { DataTable } from "@/components/common/Table";
import { columns } from "./immunizationColumn";
import { getBaseUrl } from "@/utils/urls";
import { RootState } from "@/store/store";
import { useSelector } from "react-redux";
import ImmunizationsIconDark from "@/assets/icons/immunizationDark.svg";
import ImmunizationsIconLight from "@/assets/icons/immuniztionLight.svg";
import { Typography } from "@/components/ui/typography";
import { useDashboardCardFavorite } from "@/hooks/useDashboardCardFavorite";
import { useFetchCard } from "@/hooks/useFetchCard";

interface ImmunizationsCardProps {
  shouldBlink?: boolean;
}

const ImmunizationsCard: React.FC<ImmunizationsCardProps> = ({
  shouldBlink = false,
}) => {
  const { cardVariant, isFavourite, toggleFavourite } =
    useDashboardCardFavorite("IMMUNIZATIONS");
  const memberId =
    useSelector((state: RootState) => state.search.selectedPatient?.memberId) ||
    "";

  const {
    data: immunizationsResponse,
    loading,
    error,
    isErrorRed,
  } = useFetchCard<ImmunizationsResponse["data"]>({
    endpoint: `${getBaseUrl()}/immunizations`,
    memberId,
    processData: (rawData: unknown) => (rawData as ImmunizationsResponse).data,
  });

  const immunizationsData = useMemo(() => {
    if (!immunizationsResponse?.immunizations) return [];
    return immunizationsResponse.immunizations
      .map((item, index) => ({
        id: index + 1,
        vaccine: item.vaccine,
        lastDose: item.lastDose,
      }))
      .slice(0, 10);
  }, [immunizationsResponse]);

  return (
    <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md h-fit'>
      <DashboardCardWrapper variant={cardVariant} shouldBlink={shouldBlink}>
        <DashboardCardHeader
          icon={
            <BorderedIconWrapper>
              <div className='w-4.5 h-4.5'>
                <img
                  src={ImmunizationsIconLight}
                  alt='Immunizations Light'
                  className='block dark:hidden'
                />
                <img
                  src={ImmunizationsIconDark}
                  alt='Immunizations Dark'
                  className='hidden dark:block'
                />
              </div>
            </BorderedIconWrapper>
          }
          title='Immunizations'
          className='h-[56px]'
          showStar={true}
          isFavourite={isFavourite}
          onToggleFavourite={toggleFavourite}
        />
        <CardContent className='p-4 md:h-[604px] lg:h-[520px]'>
          {error ? (
            <div className='flex items-center justify-center h-full lg:h-[520px] md:h-[604px]'>
              <Typography
                variant='medium-14'
                className={`${
                  isErrorRed
                    ? "text-red-500 dark:text-red-400"
                    : "text-grey-600 dark:text-grey-500"
                } font-medium`}>
                {error}
              </Typography>
            </div>
          ) : (
            <DataTable<Immunization>
              data={{
                data: immunizationsData,
                total: immunizationsData.length,
              }}
              columns={columns}
              height='auto'
              maxHeight={"454px"}
              isLoading={loading}
              skeletonCount={9}
            />
          )}
        </CardContent>
      </DashboardCardWrapper>
    </div>
  );
};

export default ImmunizationsCard;
